/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ROUTES } from 'constants';

import merge from 'lodash/merge';
import isNil from 'lodash/isNil';
import qs from 'qs';

const CONTENT_TYPE = 'Content-Type';
export const JSON_CONTENT_TYPE = 'application/json';
const MULTIPART_FORM_CONTENT_TYPE = 'multipart/form-data';
const CSRF_TOKEN_HEADER = 'X-Csrf-Token';

export const jsonHeaders = { 'Content-Type': JSON_CONTENT_TYPE };
export const multipartFormHeaders = { 'Content-Type': MULTIPART_FORM_CONTENT_TYPE };

const baseOptions = {
  method: 'GET',
  credentials: 'same-origin',
  headers: jsonHeaders,
};

/**
 * Provides access to Csrf Token global maintained by this utility
 * for use by other HTTP clients (e.g. @wiotp/sdk}
 */
export const getCsrfToken = () => baseOptions.headers[CSRF_TOKEN_HEADER];

const convertToJSONResponse = response => {
  if (
    response.headers &&
    response.headers.get('content-type') &&
    response.headers.get('content-type').indexOf(JSON_CONTENT_TYPE) > -1
  ) {
    return response.text().then(text => (text ? JSON.parse(text) : undefined));
  }

  if (response.status === 204) {
    return undefined;
  }

  return response;
};

/**
 * Base fetch function for API calls
 * @param {string} api request url path
 * @param {object} body request payload
 * @param {object} options contains optional parameter (example headers)
 * @param {object} queryParams request query parameter
 * @param {function(Response):Response} toJSON function to optionally convert the response object
 */
export const fetchInvoke = (api, options, queryParams, toJSON) => {
  // If we are making a request that leaves our website remove our csrf token for security
  if (api.includes('http') && !api.includes(window?.location?.hostname)) {
    // eslint-disable-next-line no-param-reassign
    delete options.headers[CSRF_TOKEN_HEADER];
  }
  if (
    (options?.body && isNil(options?.headers?.[CONTENT_TYPE])) || // need to stringify the body if it's set to application/json
    options?.headers?.[CONTENT_TYPE] === JSON_CONTENT_TYPE
  ) {
    options.body = JSON.stringify(options?.body); // eslint-disable-line no-param-reassign
  }
  // For multipart/form-data we need to let the browser figure it out
  if (options?.headers?.[CONTENT_TYPE] === MULTIPART_FORM_CONTENT_TYPE) {
    delete options.headers[CONTENT_TYPE]; // eslint-disable-line no-param-reassign
  }
  // console.log('options in invoke: ', options);
  return fetch(
    `${api.replace('?', '%3F')}${queryParams ? `?${qs.stringify(queryParams)}` : ''}`,
    options
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
  ).then(response => checkStatus(response, toJSON));
};

/**
 * Used for the GET API call
 * @param {string} api request url path
 * @param {object} options contains optional parameter (example headers)
 * @param {object} queryParams request query parameter
 * @param {function(Response):Response} toJSON function to optionally convert the response object
 */
export const fetchGet = (api, options, queryParams, toJSON = convertToJSONResponse) => {
  const opts = merge(
    {},
    baseOptions,
    options,
    options?.userEmailId ? { headers: { mam_user_email: options.userEmailId } } : {}
  );
  return fetchInvoke(api, opts, queryParams, toJSON);
};

export const logout = () => fetchGet('/logMeOut');

const checkStatus = (response, toJSON) => {
  const respStatus = response.status;

  if (response.headers.get(CSRF_TOKEN_HEADER)) {
    baseOptions.headers[CSRF_TOKEN_HEADER] = response.headers.get(CSRF_TOKEN_HEADER);
  }

  if (respStatus >= 200 && respStatus < 300) {
    return toJSON(response);
  }

  switch (respStatus) {
    case 401:
      logout().then(() => {
        if (!window.location.href.includes(ROUTES.TIMEOUT)) {
          window.location.replace(ROUTES.TIMEOUT);
        }
      });
      break;
    case 403:
      if (!window.location.href.includes(ROUTES.UNAUTHORIZED)) {
        window.location.replace(ROUTES.UNAUTHORIZED);
      }
      break;
    case 404:
      if (!window.location.href.includes(ROUTES.NOMATCH)) {
        window.location.replace(`${ROUTES.NOMATCH}?${qs.stringify({ invalidURL: response.url })}`);
      }
      break;
    default:
      return response.text().then(errText => {
        const error = new Error(response.statusText);
        try {
          error.response = JSON.parse(errText);
        } catch (e) {
          error.message = errText;
        }
        throw error;
      });
  }
  return {};
};

/**
 * Used for the POST API call
 * @param {string} api request url path
 * @param {object} body request payload
 * @param {object} options contains optional parameter (example headers)
 * @param {object} queryParams request query parameter
 * @param {function(Response):Response} toJSON function to optionally convert the response object
 */
export const fetchPost = (api, body, options, queryParams, toJSON = convertToJSONResponse) => {
  const opts = merge(
    {},
    baseOptions,
    {
      method: 'POST',
      body,
    },
    options,
    options?.userEmailId ? { headers: { mam_user_email: options.userEmailId } } : {} // Need to include user id in headers
  );
  return fetchInvoke(api, opts, queryParams, toJSON);
};

/**
 * Used for the PUT API call
 * @param {string} api request url path
 * @param {object} body request payload
 * @param {object} options contains optional parameter (example headers)
 * @param {object} queryParams request query parameter
 * @param {function(Response):Response} toJSON function to optionally convert the response object
 */
export const fetchPut = (api, body, options, queryParams, toJSON = convertToJSONResponse) => {
  const opts = merge(
    {},
    baseOptions,
    {
      method: 'PUT',
      body,
    },
    options,
    options?.userEmailId ? { headers: { mam_user_email: options.userEmailId } } : {} // Need to include user id in headers
  );

  return fetchInvoke(api, opts, queryParams, toJSON);
};

/**
 * Used for the DELETE API call
 * @param {string} api request url path
 * @param {object} body request payload
 * @param {object} options contains optional parameter (example headers)
 * @param {object} queryParams request query parameter
 * @param {function(Response):Response} toJSON function to optionally convert the response object
 */
export const fetchDelete = (api, body, options, queryParams, toJSON = convertToJSONResponse) => {
  const opts = merge(
    {},
    baseOptions,
    {
      method: 'DELETE',
      body,
    },
    options,
    options?.userEmailId ? { headers: { mam_user_email: options.userEmailId } } : {} // Need to include user id in headers
  );
  return fetchInvoke(api, opts, queryParams, toJSON);
};
